import { Component, computed, contentChild, input } from '@angular/core';
import type { AbstractControl } from '@angular/forms';

import { InputDirective } from '@shared/directives';
import { controlStateSignal } from '@shared/utils/control-state';

import { Icon } from '../icon/icon';

/** Maps a validator error key to a message, or a builder that formats the error's payload. */
export type FieldErrorMessages = Record<string, string | ((error: unknown) => string)>;

const DEFAULT_ERROR_MESSAGES: FieldErrorMessages = {
  required: 'This field is required',
  email: 'Enter a valid email',
  minlength: 'Too short',
  maxlength: 'Too long',
};

/**
 * Generic layout wrapper for any design-system form control — inputs, textareas, radio groups,
 * the file uploader, and anything added later. It renders the label column (beside the control on
 * desktop, stacked on mobile) and the reactive validation message underneath, derived from the
 * bound control's own validators. The control itself is projected, so the wrapper stays agnostic
 * about what it holds.
 */
@Component({
  selector: 'app-form-field',
  host: { class: 'form-field', '[class]': `'label-position__' + labelPosition()` },
  imports: [Icon],
  templateUrl: './form-field.html',
})
export class FormField {
  /** Text shown in the label column. Omit for a control that needs no label. */
  readonly label = input<string>('');

  /** The label alignment according to control */
  readonly labelPosition = input<'top' | 'center'>('top');

  /** Optional secondary line under the label (e.g. routing help). */
  readonly hint = input<string>('');

  /** The control that drives the validation message. */
  readonly control = input<AbstractControl | null>(null);

  /** `id` of the projected control; when set the label is associated with it for accessibility. */
  readonly controlId = input<string>('');

  /** Overrides or extends the default messages, keyed by validator error name. */
  readonly errorMessages = input<FieldErrorMessages>({});

  protected readonly state = controlStateSignal(this.control);

  /** Set when the projected control is a native `<input>`/`<textarea>`, which is the only kind that
   * cannot host the error icon itself. */
  private readonly inputControl = contentChild(InputDirective);

  protected readonly showErrorIcon = computed(
    () => !!this.inputControl() && this.state().showError,
  );

  protected readonly message = computed<string | null>(() => {
    const { errors, showError } = this.state();
    if (!showError || !errors) {
      return null;
    }

    const messages = { ...DEFAULT_ERROR_MESSAGES, ...this.errorMessages() };
    const key = Object.keys(errors).find((name) => name in messages);
    if (!key) {
      return null;
    }

    const entry = messages[key];
    return typeof entry === 'function' ? entry(errors[key]) : entry;
  });
}
